import React, { useState, useEffect, useCallback } from 'react';
import { Baby, Briefcase, Users, Scissors, Plus, Trash2, Check } from 'lucide-react';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { useSalvaCampo } from '../../utils/salvaCampo';
import { anamnesiFisiologicaAPI } from '../../services/api';
import '../../styles/SchedaFisiologica.css';

/**
 * SchedaFisiologica — parto, lavoro, famiglia, interventi chirurgici.
 *
 * PERCHE' NON SONO "VOCI DI ANAMNESI"
 * Una voce di anamnesi ha un autore, una gravita', e si dichiara
 * superata quando non vale piu'. Il tipo di parto non scade e non ha
 * gravita': e' un fatto. Percio' sta in un riquadro a campi, salvato in
 * blocco, e non nell'elenco delle voci.
 *
 * I QUATTRO RIQUADRI NON STANNO INSIEME
 * Vanno intercalati fra le voci, ognuno accanto alla sezione con cui si
 * legge: il parto dopo le terapie in corso, gli interventi chirurgici
 * dopo le patologie pregresse, la famiglia dopo l'anamnesi familiare, il
 * lavoro dopo la famiglia. Chi legge un'anamnesi non salta avanti e
 * indietro fra due blocchi.
 *
 * Percio' questo componente non disegna una schermata: tiene lo stato -
 * una riga per paziente, un salvataggio solo - e consegna i quattro
 * riquadri a chi lo contiene, che li mette dove vanno. Si usa cosi':
 *
 *   <SchedaFisiologica pazienteId={id}>
 *     {(r) => <>… {r.parto} … {r.interventi} … {r.azioni}</>}
 *   </SchedaFisiologica>
 *
 * L'alternativa era portare lo stato in AnamnesiTab e spargere la logica
 * del salvataggio in mezzo al codice delle voci.
 *
 * I CAMPI ESISTEVANO GIA'
 * `parto`, `allattamento` e `lavoro` stavano in
 * `anamnesi_fisiologica_dettaglio` con la rotta che li legge e li
 * scrive da mesi: mancava soltanto la schermata. Note, conviventi e
 * interventi sono stati aggiunti il 4 settembre 2026.
 *
 * PERCHE' IL SALVATAGGIO E' UNO SOLO E NON AUTOMATICO
 * Il riquadro scrive una riga sola per paziente: salvare a ogni tasto
 * premuto vorrebbe dire una scrittura in cartella clinica per ogni
 * lettera, con l'audit che si riempie di righe che non raccontano
 * niente. Si salva quando si preme salva, e finche' non lo si fa il
 * pulsante dice che ci sono modifiche non salvate.
 */

const PARTO = [
    { valore: 'naturale', etichetta: 'Parto naturale' },
    { valore: 'cesareo', etichetta: 'Parto cesareo' },
];

const ALLATTAMENTO = [
    { valore: 'seno', etichetta: 'Allattamento al seno' },
    { valore: 'artificiale', etichetta: 'Allattamento artificiale' },
];

/* Cinque posti, come chiesto. Il suggerimento sotto vale per tutti e
   cinque: intestare ogni casella con «marito/moglie», «figlio 1»…
   costringerebbe chi ha due figli e un genitore a incastrare i propri
   familiari in etichette sbagliate. */
const POSTI_CONVIVENTI = 5;

const messaggioErrore = (e, ripiego) =>
    e?.message || e?.response?.data?.message || ripiego;

const vuota = () => ({ data: '', descrizione: '' });

/* Le tre forme ammesse per la data di un intervento: solo anno, mese e
   anno, oppure la data intera. Chi ricorda solo l'anno non deve
   inventare un giorno. */
const DATA_PARZIALE = /^\d{4}(-\d{2}(-\d{2})?)?$/;

const SchedaFisiologica = ({ pazienteId, children }) => {
    const [dati, setDati] = useState(null);
    const [conviventi, setConviventi] = useState(Array(POSTI_CONVIVENTI).fill(''));
    const [interventi, setInterventi] = useState([vuota()]);
    const [caricamento, setCaricamento] = useState(true);
    const [salvataggio, setSalvataggio] = useState(false);
    /* «Modificato» e' un confronto con l'istantanea presa al
       caricamento e a ogni salvataggio (`versione` sale li'), non un
       flag alzato a ogni tasto: vedi utils/uscitaProtetta.js. */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ dati, conviventi, interventi }, versione);
    useUscitaProtetta(sporco);
    const [salvato, setSalvato] = useState(false);
    const [errore, setErrore] = useState(null);

    /* IL SALVATAGGIO AUTOMATICO, un campo per volta, quando il medico
       esce dal campo. Il pulsante «Salva» resta: e' il modo di riprovare
       tutto insieme quando piu' campi sono rossi. Regole e perche' in
       utils/salvaCampo.js. */
    const { stato: statoCampi, salva: salvaCampo, nonSalvati } = useSalvaCampo(
        useCallback((campo, valore) => anamnesiFisiologicaAPI.saveDettaglio(
            pazienteId, { [campo]: valore }), [pazienteId]),
        /* Quel pezzo e' in archivio: l'istantanea si rifa', altrimenti la
           barra continuerebbe a dire «modifiche non salvate» per un
           modulo che e' stato salvato campo per campo. Quello che NON e'
           passato resta rosso e si conta da solo. */
        useCallback(() => setVersione((v) => v + 1), []));

    /* La spunta e il bordo rosso accanto a un campo. */
    const segno = (campo) => {
        const st = statoCampi[campo];
        if (!st) return null;
        if (st === 'in_corso') return <span className="sf-segno sf-segno--corso" title="Sto salvando…">…</span>;
        if (st === 'salvato') return <span className="sf-segno sf-segno--ok" title="Salvato"><Check size={13} aria-hidden="true" /></span>;
        return (
            <button type="button" className="sf-segno sf-segno--no"
                    title="Non salvato: clicca per riprovare"
                    onClick={() => salvaCampo(campo, valoreDi(campo))}>non salvato</button>
        );
    };
    const classeCampo = (campo) => (statoCampi[campo] === 'non_salvato' ? 'sf-testo--rosso' : '');

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await anamnesiFisiologicaAPI.getDettaglio(pazienteId);
            /* `dettaglio` e' null quando il paziente non ha ancora
               nessuna anamnesi: e' il caso normale la prima volta, non un
               errore. Il salvataggio crea la riga da se'. */
            const d = r.data?.data?.dettaglio || {};
            setDati(d);

            /* Le cinque caselle si riempiono con quello che c'e' e si
               completano con caselle vuote: chi ne aveva scritti due
               deve trovare tre posti liberi, non due caselle e basta. */
            const salvati = Array.isArray(d.conviventi) ? d.conviventi : [];
            setConviventi([
                ...salvati,
                ...Array(Math.max(0, POSTI_CONVIVENTI - salvati.length)).fill(''),
            ]);

            const inter = Array.isArray(d.interventi) ? d.interventi : [];
            setInterventi(inter.length
                ? inter.map((x) => ({ data: x?.data || '', descrizione: x?.descrizione || '' }))
                : [vuota()]);
            setVersione((v) => v + 1);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Non riesco a leggere la scheda'));
        } finally {
            setCaricamento(false);
        }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    /* Il valore di adesso, per il ritentativo: gli elenchi non stanno in
       `dati` ma nei loro stati. */
    const valoreDi = (campo) => {
        if (campo === 'conviventi') return conviventi;
        if (campo === 'interventi') return interventi;
        return dati?.[campo] ?? null;
    };

    const cambia = (campo, valore) => {
        setDati((p) => ({ ...(p || {}), [campo]: valore }));
        setSalvato(false);
    };

    /* Ripremendo la scelta già scelta la si annulla: senza, chi tocca
       «cesareo» per sbaglio non ha nessun modo di tornare a «non
       indicato», e in cartella resta un dato che nessuno ha dichiarato. */
    const scegli = (campo, valore) => {
        const nuovo = dati?.[campo] === valore ? null : valore;
        cambia(campo, nuovo);
        /* Una spunta non ha un'uscita dal campo: si salva al clic. */
        salvaCampo(campo, nuovo);
    };

    const cambiaConvivente = (i, v) => {
        setConviventi((p) => p.map((x, k) => (k === i ? v : x)));
        setSalvato(false);
    };

    const cambiaIntervento = (i, campo, v) => {
        setInterventi((p) => p.map((x, k) => (k === i ? { ...x, [campo]: v } : x)));
        setSalvato(false);
    };

    const aggiungiIntervento = () => {
        setInterventi((p) => [...p, vuota()]);
    };

    const togliIntervento = (i) => {
        setInterventi((p) => (p.length === 1 ? [vuota()] : p.filter((_, k) => k !== i)));
        setSalvato(false);
    };

    /* Gli interventi sono una colonna sola (jsonb): si salva l'elenco
       intero. Una data scritta male non parte - il server la
       rifiuterebbe - e il messaggio resta quello di sempre, sotto. */
    const salvaInterventi = () => {
        const sbagliate = interventi.some((x) => x.data && !DATA_PARZIALE.test(String(x.data).trim()));
        if (sbagliate) return;
        salvaCampo('interventi', interventi);
    };

    const dateSbagliate = interventi
        .map((x, i) => ({ i, ok: !x.data || DATA_PARZIALE.test(x.data.trim()) }))
        .filter((x) => !x.ok)
        .map((x) => x.i);

    const salva = async () => {
        if (dateSbagliate.length) {
            setErrore('Le date degli interventi vanno scritte come 2019, 2019-03 '
                    + 'oppure 2019-03-14.');
            return;
        }
        try {
            setSalvataggio(true);
            setErrore(null);
            await anamnesiFisiologicaAPI.saveDettaglio(pazienteId, {
                ...(dati || {}),
                conviventi,
                interventi,
            });
            setSalvato(true);
            setTimeout(() => setSalvato(false), 3000);
            await carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Non riesco a salvare la scheda'));
        } finally {
            setSalvataggio(false);
        }
    };

    if (caricamento) return null;   // le voci si vedono comunque

    /* ---------- Parto e allattamento ---------- */
    const bloccoParto = (
            <section className="sf-riquadro">
                <h3><Baby size={16} strokeWidth={1.8} aria-hidden="true" /> Parto</h3>

                <div className="sf-riga-scelta">
                    <div className="sf-spunte">
                        {PARTO.map((o) => (
                            <label key={o.valore} className="sf-spunta">
                                <input type="checkbox"
                                    checked={dati?.parto === o.valore}
                                    onChange={() => scegli('parto', o.valore)} />
                                <span>{o.etichetta}</span>
                            </label>
                        ))}
                    </div>
                    <input className={`sf-testo ${classeCampo('parto_note')}`}
                        placeholder="Note sul parto"
                        value={dati?.parto_note || ''}
                        onChange={(e) => cambia('parto_note', e.target.value)}
                        onBlur={(e) => salvaCampo('parto_note', e.target.value || null)} />
                    {segno('parto_note')}
                </div>

                <div className="sf-riga-scelta">
                    <div className="sf-spunte">
                        {ALLATTAMENTO.map((o) => (
                            <label key={o.valore} className="sf-spunta">
                                <input type="checkbox"
                                    checked={dati?.allattamento === o.valore}
                                    onChange={() => scegli('allattamento', o.valore)} />
                                <span>{o.etichetta}</span>
                            </label>
                        ))}
                    </div>
                    <input className={`sf-testo ${classeCampo('allattamento_note')}`}
                        placeholder="Note sull'allattamento"
                        value={dati?.allattamento_note || ''}
                        onChange={(e) => cambia('allattamento_note', e.target.value)}
                        onBlur={(e) => salvaCampo('allattamento_note', e.target.value || null)} />
                    {segno('allattamento_note')}
                </div>
            </section>
    );

    /* ---------- Lavoro ---------- */
    const bloccoLavoro = (
            <section className="sf-riquadro">
                <h3><Briefcase size={16} strokeWidth={1.8} aria-hidden="true" /> Lavoro</h3>
                <input className={`sf-testo sf-testo--pieno ${classeCampo('lavoro')}`}
                    placeholder="Attività lavorativa, turni, esposizioni"
                    value={dati?.lavoro || ''}
                    onChange={(e) => cambia('lavoro', e.target.value)}
                    onBlur={(e) => salvaCampo('lavoro', e.target.value || null)} />
                {segno('lavoro')}
            </section>
    );

    /* ---------- Famiglia ---------- */
    const bloccoFamiglia = (
            <section className="sf-riquadro">
                <h3><Users size={16} strokeWidth={1.8} aria-hidden="true" /> Famiglia</h3>
                <span className="sf-etichetta">Conviventi</span>
                <div className="sf-conviventi">
                    {conviventi.map((v, i) => (
                        <input key={i} className="sf-testo"
                            value={v}
                            onChange={(e) => cambiaConvivente(i, e.target.value)}
                            onBlur={() => salvaCampo('conviventi', conviventi)} />
                    ))}
                </div>
                <span className="sf-aiuto">marito / moglie, figli, altri</span>
                <input className={`sf-testo sf-testo--pieno ${classeCampo('famiglia_note')}`}
                    placeholder="Altro sulla situazione familiare"
                    value={dati?.famiglia_note || ''}
                    onChange={(e) => cambia('famiglia_note', e.target.value)}
                    onBlur={(e) => salvaCampo('famiglia_note', e.target.value || null)} />
                {segno('famiglia_note')}
            </section>
    );

    /* ---------- Interventi chirurgici ---------- */
    const bloccoInterventi = (
            <section className="sf-riquadro">
                <h3><Scissors size={16} strokeWidth={1.8} aria-hidden="true" /> Interventi chirurgici</h3>
                <span className="sf-aiuto">
                    La data può essere incompleta: <b>2019</b>, <b>2019-03</b> oppure
                    <b> 2019-03-14</b>. Chi ricorda solo l&apos;anno non deve inventare un giorno.
                </span>

                <div className="sf-interventi">
                    {interventi.map((x, i) => (
                        <div key={i} className="sf-intervento">
                            <input
                                className={`sf-testo sf-data ${dateSbagliate.includes(i) ? 'errore' : ''}`}
                                placeholder="2019-03"
                                value={x.data}
                                onChange={(e) => cambiaIntervento(i, 'data', e.target.value)}
                                onBlur={() => salvaInterventi()} />
                            <input className="sf-testo"
                                placeholder="Intervento, struttura, note"
                                value={x.descrizione}
                                onChange={(e) => cambiaIntervento(i, 'descrizione', e.target.value)}
                                onBlur={() => salvaInterventi()} />
                            <button type="button" className="sf-toglie"
                                onClick={() => togliIntervento(i)}
                                title="Togli questa riga">
                                <Trash2 size={15} aria-hidden="true" />
                            </button>
                        </div>
                    ))}
                </div>

                <button type="button" className="sf-btn" onClick={aggiungiIntervento}>
                    <Plus size={15} aria-hidden="true" /> Aggiungi un intervento
                </button>
            </section>
    );

    /* ---------- La barra del salvataggio ----------
       Compare solo quando c'e' qualcosa da salvare, e resta attaccata in
       basso: con i quattro riquadri sparsi fra le voci, un pulsante in
       fondo alla pagina lo vedrebbe solo chi scorre fino in fondo, e chi
       scrive nel parto - che sta in alto - se ne andrebbe senza salvare. */
    const bloccoAzioni = (nonSalvati.length || sporco || salvato || errore) ? (
        <div className={`sf-azioni ${(nonSalvati.length || sporco) ? 'da-salvare' : ''}`}>
            {errore && <span className="sf-errore-riga">{errore}</span>}
            {!nonSalvati.length && salvato && !sporco && (
                <span className="sf-salvato"><Check size={15} aria-hidden="true" /> Salvato</span>
            )}
            {/* IL CONTO DEI CAMPI ROSSI. Non sparisce da solo: finche'
                e' li', c'e' del testo che non e' in cartella. */}
            {nonSalvati.length > 0 && (
                <span className="sf-sporco">
                    {nonSalvati.length === 1 ? 'Un campo non salvato' : `${nonSalvati.length} campi non salvati`}
                </span>
            )}
            {!nonSalvati.length && sporco && <span className="sf-sporco">Modifiche non salvate</span>}
            {(nonSalvati.length > 0 || sporco) && (
                <button type="button" className="sf-btn sf-btn--principale"
                    onClick={salva} disabled={salvataggio}>
                    {salvataggio ? 'Salvo…' : (nonSalvati.length ? 'Riprova a salvare' : 'Salva')}
                </button>
            )}
        </div>
    ) : null;

    /* I nomi delle chiavi sono quelli che usa chi ci contiene; le
       costanti si chiamano `blocco...` perche' `interventi` e' gia' lo
       stato dell'elenco, qualche riga sopra. */
    return children({
        parto: bloccoParto,
        lavoro: bloccoLavoro,
        famiglia: bloccoFamiglia,
        interventi: bloccoInterventi,
        azioni: bloccoAzioni,
    });
};

export default SchedaFisiologica;
